import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";

import { AlertIcon, CheckIcon, CloseIcon, InfoIcon } from "./icons";
import styles from "./StateMessage.module.css";

/**
 * Three kinds of notice, one pattern (UX gate round 1, #3): `neutral` says
 * what is so, `success` that a gesture went through, `conflict` that it did
 * not — the last one with the status red at its edge **and** a symbol beside
 * the word, never the colour alone. `success` stays in ink: a gesture that
 * worked is no status in the sense of PRD §18.2, and the data area keeps
 * `--st-*` for the drawing and its findings. Every tone carries its symbol —
 * `neutral` an „i" in --ink-2 (round-2 screen check, R2-9): without one its
 * text started 28 px further left than a success's, and a list of mixed
 * notices had no common edge.
 */
type Tone = "neutral" | "success" | "conflict";

/**
 * What a notice asks of the screen around it: has its first paint happened?
 * `null` outside a `FirstPaintScope` — then every notice is one that arrives,
 * and reveals as it always did. Inside one it is a flag that turns `true`
 * after the scope's first commit; a notice reads it once, when it mounts, and
 * is not re-rendered when it turns.
 */
const PaintContext = createContext<RefObject<boolean> | null>(null);

/**
 * Marks a screen whose notices are standing when it first appears. A notice
 * with `reveal` that is already here when the scope first renders shows at
 * once — `@starting-style` fires on every first insertion, so without this a
 * banner that was part of the page on arrival (a calculation opened with its
 * „Preis fehlt“) would slide the figures under it down over `--dur-3` while a
 * person is already reading them. BW-6 is for the notice that arrives *after*
 * an answer: those mount once the scope's first commit is through and reveal
 * as usual.
 *
 * It renders no element of its own, so the layout of its children does not
 * change, and it renders nothing again when it settles: the flag is a plain
 * ref set in an effect, not state — a notice captures what it saw
 * when it mounted and never changes its mind (see `StateMessage`), so one
 * wrapper stays one wrapper across re-renders and nothing remounts.
 */
export function FirstPaintScope({ children }: { children: ReactNode }) {
  const done = useRef(false);
  useEffect(() => {
    done.current = true;
  }, []);
  return <PaintContext value={done}>{children}</PaintContext>;
}

interface StateMessageProps {
  title: string;
  /** What to do next. An empty or failed screen is an instruction, not an apology. */
  children?: ReactNode;
  tone?: Tone;
  action?: ReactNode;
  /**
   * Whether this box announces itself as a live region. Default `true` —
   * most callers are the only thing on the screen when they mount, and this
   * is the one place that says so. Pass `false` where the same sentence
   * already reaches a persistent live region elsewhere (fix round 2, item
   * 8): a `role="status"` mounted together with its own text is not
   * reliably announced — a screen reader reacts to a *change* inside a
   * region that was already there, not to a brand new one arriving already
   * full. `"alert"` for a refusal of the server that has to interrupt — the
   * one `role="alert"` block the global constraints allow.
   */
  live?: boolean | "alert";
  /** Where a button's `aria-describedby` points at this notice. */
  id?: string;
  /**
   * The notice can be put away (UX gate round 1, #3: „mit × oder Handlung").
   * The caller decides where the focus goes afterwards — the button that
   * had it is gone.
   */
  onDismiss?: () => void;
  /**
   * The ×'s accessible name where one screen holds several notices — „Schließen:
   * <the sentence>" on the start page, so a list of them is not a list of
   * identical buttons. Default „Meldung schließen".
   */
  dismissLabel?: string;
  /** For a caller that puts the focus on a notice's × (the next one, after one is closed). */
  dismissRef?: Ref<HTMLButtonElement>;
  /**
   * With a ×, the action stands beside the sentence (between it and the ×)
   * and drops under it at 600 px, where the × stays top right (the start
   * page's „Zum Vorgang"). `true` puts the × before the action in the DOM —
   * the caller passes it for the narrow layout, so Tab follows what the eye
   * reads there and never climbs back up (Task 13 fix round 1).
   */
  dismissFirst?: boolean;
  /**
   * The notice's own symbol instead of the tone's (UX gate round 2, #4): the
   * start page's outcomes say *what* happened — taken over, declined, lapsed —
   * with the same text edge for all. Decorative like every symbol here; the
   * sentence carries the meaning.
   */
  icon?: ReactNode;
  /**
   * Asks for something: an ink edge and an ink symbol on a neutral notice
   * (round 2, #4) — a declined or lapsed request leaves the Vorgang with me,
   * and that notice must not stand quieter than one that needs nothing.
   */
  strong?: boolean;
  /**
   * The notice opens over `--dur-3` instead of appearing at once (BW-6): for
   * one that arrives *after* something happened — a calculation's answer —
   * where a box popping in above the figures shoves them down unannounced.
   * The message is wrapped twice (`reveal` animates the row, `revealClip`
   * holds the message and clips it while the row is shut); its live-region
   * role stays on the message itself, not on a wrapper (a region is the thing
   * that holds the words). In a column with a `gap`, the container sets
   * `--reveal-gap` to that gap so the stack does not jump by one gap when the
   * wrapper appears (see `StateMessage.module.css`). Off by default, so the
   * dozen screens that show this on first paint keep their markup and stand
   * still. Inside a `FirstPaintScope`, a notice that was already there when
   * the scope first rendered keeps the wrapper but shows at once
   * (`data-instant`): the opening is for what *arrives*, not for what the
   * screen came with.
   */
  reveal?: boolean;
}

export function StateMessage({
  title,
  children,
  tone = "neutral",
  action,
  live = true,
  id,
  onDismiss,
  dismissLabel = "Meldung schließen",
  dismissRef,
  dismissFirst = false,
  icon,
  strong = false,
  reveal = false,
}: StateMessageProps) {
  // Captured once, at mount: a notice that was part of its screen's first
  // paint shows at once, and stays that way. Reading the flag on every render
  // instead would flip the wrapper's attribute when the scope settles.
  const paint = useContext(PaintContext);
  const [instant] = useState(() => paint !== null && !paint.current);
  const Icon = tone === "conflict" ? AlertIcon : tone === "success" ? CheckIcon : InfoIcon;
  const beside = onDismiss !== undefined && action !== undefined && action !== null;
  const dismiss =
    onDismiss !== undefined ? (
      <button
        key="dismiss"
        ref={dismissRef}
        type="button"
        className={styles.dismiss}
        aria-label={dismissLabel}
        title="Schließen"
        onClick={onDismiss}
      >
        <CloseIcon />
      </button>
    ) : null;
  const side = beside ? (
    <div key="side" className={styles.side}>
      {action}
    </div>
  ) : null;
  const message = (
    <div
      id={id}
      className={[
        styles.box,
        tone === "conflict" ? styles.conflict : "",
        tone === "success" ? styles.success : "",
        strong ? styles.strong : "",
        styles.withIcon,
        onDismiss !== undefined ? styles.withDismiss : "",
        beside ? styles.withSide : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role={live === "alert" ? "alert" : live ? "status" : undefined}
    >
      <span className={styles.icon}>{icon ?? <Icon />}</span>
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        {/* A `div`, not a `p`. Callers put buttons and their own wrappers in
            here — `PlanningScreen`'s „Verwerfen / Trotzdem laden" pair, for one
            — and a `div` inside a `p` is invalid HTML that React reports as a
            hydration error on the console. It has been doing so since
            18.08.2026 (`e6371c9`); found again in the acceptance walk of
            30.08.2026 (finding 15). The paragraph's own styling stays. */}
        {children ? <div className={styles.body}>{children}</div> : null}
        {action && !beside ? <div className={styles.action}>{action}</div> : null}
      </div>
      {dismissFirst ? [dismiss, side] : [side, dismiss]}
    </div>
  );
  return reveal ? (
    <div className={styles.reveal} data-instant={instant ? "" : undefined}>
      <div className={styles.revealClip}>{message}</div>
    </div>
  ) : message;
}
